/**
 * Plays a window growing from its wall card and returning into it (Part 10
 * Rule 3; prototype in docs/specs/2026-10-21-card-open-match.html). A
 * `from-card` motion starts the grow; `returning` (a person's close, see
 * `lib/focus/focus-card-return.ts`) sends it back to the card's current
 * rect, read again then. One critically damped spring drives both ways
 * from its current value and velocity, so a close mid-open reverses
 * smoothly. Each frame writes only transform, opacity and clip-path on the
 * window's layers (`card-grow-body.tsx`) and the shadow plate's height.
 * Reduced motion (checked once per open) cross-fades in 150ms instead.
 * A return that has not settled after `RETURN_FALLBACK_MS` finishes anyway.
 */
import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { visibleWallCard } from "@/components/wall/wall-view-adapter";
import type { FocusWindowBox } from "@/lib/focus-window-layout";
import type { FocusCardOrigin, FocusMotion } from "@/lib/focus/focus-types";
import { springSettled, stepSpring } from "@/lib/motion/spring";
import { cardGrowFrame, type CardGrowRect } from "./card-grow-geometry";

/** The reduced-motion cross-fade (seconds). */
const REDUCED_FADE = 0.15;
/** A long frame (a busy main thread) advances the motion at most this far. */
const MAX_FRAME = 0.032;
/**
 * A return still running this long after it started (ms) finishes at once:
 * a hidden or occluded window gets no animation frames, and the card must
 * not stay hidden behind it.
 */
export const RETURN_FALLBACK_MS = 1000;

/** Grows already played here: a remounted window does not grow again. */
const played = new WeakSet<FocusMotion>();

/** What the window draws while it moves: the card it moves from or to. */
export interface CardGrowFlight {
  card: CardGrowRect;
  /** The card's type size (`--wall-fs`) for its snapshot. */
  fontSize: number;
  reduce: boolean;
}

/** The window body's layers, outermost first. */
export interface CardGrowLayers {
  /** Carries the transform (and the reduced-motion opacity). */
  motion: RefObject<HTMLDivElement | null>;
  /** The shadow at the visible height, outside the clip. */
  plate: RefObject<HTMLDivElement | null>;
  clip: RefObject<HTMLDivElement | null>;
  live: RefObject<HTMLDivElement | null>;
  snapshot: RefObject<HTMLDivElement | null>;
}

interface GrowSpring {
  value: number;
  velocity: number;
  /** 1 opens, 0 returns into the card. */
  target: 0 | 1;
  card: CardGrowRect;
  reduce: boolean;
}

interface UseCardGrowInput {
  frameId: string;
  box: FocusWindowBox;
  motion: FocusMotion | undefined;
  returning: boolean;
  rootRef: RefObject<HTMLDivElement | null>;
  /** Each frame's progress (0 = the card, 1 = open), for the backdrop. */
  onProgress?: (progress: number) => void;
  /** The return settled, or there is no card to return into. */
  onReturned?: () => void;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** A card's client rect in the coordinates of the window's surface. */
function onSurface(
  origin: FocusCardOrigin,
  root: HTMLElement | null,
): CardGrowRect | null {
  const surface = root?.parentElement;
  if (!surface) return null;
  const at = surface.getBoundingClientRect();
  const { rect } = origin;
  return {
    x: rect.left - at.left,
    y: rect.top - at.top,
    width: rect.width,
    height: rect.height,
  };
}

function advance(spring: GrowSpring, dt: number): void {
  if (spring.reduce) {
    const delta = dt / REDUCED_FADE;
    spring.value =
      spring.target > spring.value
        ? Math.min(spring.target, spring.value + delta)
        : Math.max(spring.target, spring.value - delta);
    return;
  }
  const next = stepSpring(spring, spring.target, dt);
  spring.value = next.value;
  spring.velocity = next.velocity;
}

function settled(spring: GrowSpring): boolean {
  return spring.reduce
    ? spring.value === spring.target
    : springSettled(spring, spring.target);
}

function paint(
  layers: CardGrowLayers,
  spring: GrowSpring,
  box: FocusWindowBox,
): void {
  const body = layers.motion.current;
  if (spring.reduce) {
    if (body) body.style.opacity = String(spring.value);
    return;
  }
  const frame = cardGrowFrame(spring.value, spring.card, box);
  if (body) body.style.transform = frame.transform;
  const clip = layers.clip.current;
  if (clip) clip.style.clipPath = frame.clipPath;
  const plate = layers.plate.current;
  if (plate) {
    plate.style.height = `${frame.visibleHeight}px`;
    plate.style.borderRadius = `${frame.radius}px`;
  }
  const snapshot = layers.snapshot.current;
  const live = layers.live.current;
  if (snapshot) {
    snapshot.style.transform = `scale(${frame.snapshotScale})`;
    snapshot.style.opacity = String(frame.snapshotOpacity);
  }
  if (live) live.style.opacity = snapshot ? String(frame.liveOpacity) : "";
}

/** The window rests open: no layer keeps a motion style. */
function clear(layers: CardGrowLayers): void {
  const body = layers.motion.current;
  if (body) {
    body.style.transform = "";
    body.style.opacity = "";
  }
  if (layers.clip.current) layers.clip.current.style.clipPath = "";
  if (layers.live.current) layers.live.current.style.opacity = "";
}

export function useCardGrow({
  frameId,
  box,
  motion,
  returning,
  rootRef,
  onProgress,
  onReturned,
}: UseCardGrowInput): {
  flight: CardGrowFlight | null;
  layers: CardGrowLayers;
} {
  const motionRef = useRef<HTMLDivElement>(null);
  const plateRef = useRef<HTMLDivElement>(null);
  const clipRef = useRef<HTMLDivElement>(null);
  const liveRef = useRef<HTMLDivElement>(null);
  const snapshotRef = useRef<HTMLDivElement>(null);
  const layers = useMemo<CardGrowLayers>(
    () => ({
      motion: motionRef,
      plate: plateRef,
      clip: clipRef,
      live: liveRef,
      snapshot: snapshotRef,
    }),
    [],
  );
  const [flight, setFlight] = useState<CardGrowFlight | null>(null);
  const springRef = useRef<GrowSpring | null>(null);
  // The running frames read the latest box and callbacks.
  const latest = useRef({ box, onProgress, onReturned });
  useLayoutEffect(() => {
    latest.current = { box, onProgress, onReturned };
  });
  // A window gone mid-flight leaves nothing half dimmed.
  useLayoutEffect(() => {
    const progress = latest;
    return () => progress.current.onProgress?.(1);
  }, []);

  // A card click: start at the card.
  useLayoutEffect(() => {
    if (motion?.kind !== "from-card" || played.has(motion)) return;
    played.add(motion);
    const card = onSurface(motion, rootRef.current);
    if (!card) return;
    const reduce = prefersReducedMotion();
    springRef.current = { value: 0, velocity: 0, target: 1, card, reduce };
    setFlight({ card, fontSize: motion.fontSize, reduce });
  }, [motion, rootRef]);

  // A person's close: back into the card where it is now; reopened, out again.
  useLayoutEffect(() => {
    const spring = springRef.current;
    if (!returning) {
      if (spring?.target !== 0) return;
      spring.target = 1;
      // Restarts the frames should the return have settled already.
      setFlight((current) => current && { ...current });
      return;
    }
    const origin = visibleWallCard(frameId);
    const card = origin && onSurface(origin, rootRef.current);
    if (!origin || !card) {
      latest.current.onReturned?.();
      return;
    }
    const next: GrowSpring = spring ?? {
      value: 1,
      velocity: 0,
      target: 0,
      card,
      reduce: prefersReducedMotion(),
    };
    next.target = 0;
    next.card = card;
    springRef.current = next;
    setFlight({ card, fontSize: origin.fontSize, reduce: next.reduce });
    const fallback = window.setTimeout(
      () => latest.current.onReturned?.(),
      RETURN_FALLBACK_MS,
    );
    return () => window.clearTimeout(fallback);
  }, [frameId, returning, rootRef]);

  // The frames: from the spring's current value and velocity to its target.
  useLayoutEffect(() => {
    const spring = springRef.current;
    if (!flight || !spring) return;
    let raf = 0;
    let last = performance.now();
    const draw = () => {
      paint(layers, spring, latest.current.box);
      latest.current.onProgress?.(spring.value);
    };
    const tick = (now: number) => {
      advance(spring, Math.min(MAX_FRAME, Math.max(0, (now - last) / 1000)));
      last = now;
      if (!settled(spring)) {
        draw();
        raf = requestAnimationFrame(tick);
        return;
      }
      spring.value = spring.target;
      spring.velocity = 0;
      if (spring.target === 1) {
        clear(layers);
        latest.current.onProgress?.(1);
        setFlight(null);
        return;
      }
      draw();
      latest.current.onReturned?.();
    };
    draw();
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [flight, layers]);

  return { flight, layers };
}
